@file:Suppress("TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.promocodes

import com.nonopn.skydome.common.app.api.BackendErrorException
import com.nonopn.skydome.common.app.api.JsonProvider
import com.nonopn.skydome.common.app.api.impl.AdminPromotionCode
import com.nonopn.skydome.common.app.api.impl.ApiAdmin
import com.nonopn.skydome.common.app.api.impl.ApiErrorWithConflicts
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skywatcher.app.utils.safeLaunch
import eu.codlab.viewmodel.StateViewModel
import kotlinx.coroutines.CancellationException

/** Why the save failed, when no field of the form is concerned. */
enum class PromoSaveFailure { NotFound, Network, Server }

data class PromoCodeEditState(
    val form: PromoCodeForm = PromoCodeForm(),
    // Local errors are shown once "Save" was pressed (then updated at each change)
    val showErrors: Boolean = false,
    // Messages of the server per field (409 InvalidRequest, conflicts[]), cleared when the field changes
    val serverErrors: Map<PromoField, String> = emptyMap(),
    val saving: Boolean = false,
    val saveFailure: PromoSaveFailure? = null,
    val serverMessage: String? = null,
    val saved: AdminPromotionCode? = null,
)

/**
 * Form of a promotion code: creation ([existing] null) or modification. The POST always sends the COMPLETE code
 * (every field read from the GET, then edited): a field left out would be reset by the server.
 */
class PromoCodeEditModel(
    private val appModel: AppModel,
    existing: AdminPromotionCode?,
) : StateViewModel<PromoCodeEditState>(
    PromoCodeEditState(form = existing?.toForm(promoLocalOffsetSeconds()) ?: PromoCodeForm())
) {
    private val locked: Boolean = existing?.referrerLocked() == true

    private fun api(): ApiAdmin? = appModel.states.value.apiProvider?.apiAdmin as? ApiAdmin

    /** Changes the form; the server error of the changed [field] (if any) is cleared. */
    fun edit(field: PromoField?, change: PromoCodeForm.() -> PromoCodeForm) = updateState {
        copy(
            form = form.change(),
            serverErrors = if (field == null) serverErrors else serverErrors - field,
            saveFailure = null,
            serverMessage = null,
        )
    }

    fun validation(): PromoValidation =
        validatePromoCodeForm(states.value.form, promoNowSeconds(), promoLocalOffsetSeconds())

    fun save() {
        if (states.value.saving) return
        // Referrer without email: the POST would erase the referral
        if (locked) return
        val validation = validation()
        val toSave = validation.save
        if (toSave == null) {
            updateState { copy(showErrors = true) }
            return
        }
        val api = api()
        if (api == null) {
            updateState { copy(showErrors = true, saveFailure = PromoSaveFailure.Network) }
            return
        }
        updateState { copy(showErrors = true, saving = true, saveFailure = null, serverMessage = null) }
        safeLaunch {
            try {
                val saved = api.savePromotionCode(toSave.toRequest())
                PromoCodesAdminStore.upsert(saved)
                updateState { copy(saving = false, saved = saved) }
            } catch (e: CancellationException) {
                throw e
            } catch (e: BackendErrorException) {
                val error = try {
                    JsonProvider.json.decodeFromString<ApiErrorWithConflicts>(e.body)
                } catch (_: Throwable) {
                    null
                }
                val perField = linkedMapOf<PromoField, String>()
                val others = mutableListOf<String>()
                error?.conflicts.orEmpty().forEach { conflict ->
                    val field = promoFieldOfConflict(conflict.field)
                    if (field != null && field !in perField) perField[field] = conflict.message else others += conflict.message
                }
                val failure = when {
                    perField.isNotEmpty() && others.isEmpty() -> null
                    error?.code == "PromotionCodeNotFound" -> PromoSaveFailure.NotFound
                    else -> PromoSaveFailure.Server
                }
                updateState {
                    copy(
                        saving = false,
                        serverErrors = perField,
                        saveFailure = failure,
                        serverMessage = others.filter { it.isNotBlank() }.joinToString("\n")
                            .ifBlank { error?.message?.takeIf { failure != null && it.isNotBlank() } },
                    )
                }
            } catch (_: Throwable) {
                updateState { copy(saving = false, saveFailure = PromoSaveFailure.Network) }
            }
        }
    }
}
